import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SFStack } from "../grid";
import SFLink from "../link";
import getStyled from "../../hooks/getStyled";
import SFTypography from "../typography";
import SFChip from "../chip";
import { FONT_FAMILIES } from "../../theme/styles";
import { getFormattedPathUrl } from "../../helpers/pathFunctions";

const { styled } = getStyled();

const SFLinkStyled = styled(SFLink)(({ theme }) => ({
  cursor: "pointer",
  "&.MuiTypography-root": {
    letterSpacing: "1px",
    lineHeight: "14px",
  },
}));

const SFTypographyStyled = styled(SFTypography)(({ theme }) => ({
  letterSpacing: "1px",
  lineHeight: "14px",
}));

const SFChipStyled = styled(SFChip)(({ theme }) => ({
  height: 14,
  "& .MuiChip-label": {
    ...FONT_FAMILIES.LIGHT,
    fontSize: 9,
    lineHeight: "9px",
    letterSpacing: "0.5px",
    color: theme.palette.blue400.main,
    paddingLeft: 8,
    paddingRight: 8,
  },
}));

const Breadcrumb = ({
  isLastBreadcrumb,
  path,
  label,
  editing,
  shouldTruncate = false,
  currentRouteParams,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (shouldTruncate) {
    label = `${label.substring(0, 24)}...`;
  }
  if (isLastBreadcrumb) {
    return (
      <SFStack
        direction="row"
        alignItems="center"
        spacing={0.625} //5px
      >
        <SFTypographyStyled variant="body2bold" color="grey450" component="h1">
          {label}
        </SFTypographyStyled>
        {editing && (
          <SFChipStyled
            color="grey100"
            label={t("components.breadcrumbs.editingLabel")}
          />
        )}
      </SFStack>
    );
  }
  return (
    <SFLinkStyled
      underline="hover"
      variant="body2"
      color="grey400"
      onClick={() => navigate(getFormattedPathUrl(path, currentRouteParams))}
    >
      {label}
    </SFLinkStyled>
  );
};

Breadcrumb.propTypes = {
  /** Whether the breadcrumb is the last one. */
  isLastBreadcrumb: PropTypes.bool,
  /** The path of the breadcrumb. */
  path: PropTypes.string,
  /** The label of the breadcrumb. */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Whether the breadcrumb is in editing mode.
   * In this case we display an editing pill. */
  editing: PropTypes.bool,
  /** Whether the breadcrumb should be truncated.
   * If label is more than 24 characters the breadcrumb text is truncated and a tooltip is been displayed in hover to view the full text. */
  shouldTruncate: PropTypes.bool,
  /**The current route params.
   *  These params will be used to replace another's route params in breadcrumb.
   */
  currentRouteParams: PropTypes.object,
};

export default Breadcrumb;
